import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Channel } from '../data/mockData';
import { toast } from 'sonner';

interface EditChannelDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (channel: Omit<Channel, 'id' | 'stationId'>) => void;
  onDelete: () => void;
  channel: Channel;
  existingChannelNumbers: number[];
}

export function EditChannelDrawer({
  isOpen,
  onClose,
  onSave,
  onDelete,
  channel,
  existingChannelNumbers,
}: EditChannelDrawerProps) {
  const [channelName, setChannelName] = useState(channel.name);
  const [status, setStatus] = useState<'Active' | 'Suspended'>(channel.status);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setChannelName(channel.name);
    setStatus(channel.status);
  }, [channel]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!channelName.trim()) {
      newErrors.channelName = 'Channel name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    onSave({
      channelNumber: channel.channelNumber,
      name: channelName.trim(),
      status,
    });

    toast.success('Channel updated successfully');
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="w-full sm:max-w-xl bg-gray-900 border-gray-800 text-white overflow-y-auto p-0"
      >
        <SheetHeader className="px-6 sm:px-8 pt-6 sm:pt-8 pb-5 sm:pb-6 border-b border-gray-800">
          <SheetTitle className="text-2xl font-bold text-white">Edit Channel</SheetTitle>
          <SheetDescription className="text-gray-400 mt-2">
            Update channel information
          </SheetDescription>
        </SheetHeader>

        <div className="px-6 sm:px-8 py-6 sm:py-8 space-y-6">
          {/* Channel Name */}
          <div className="space-y-3 max-w-md">
            <Label htmlFor="channelName" className="text-sm font-semibold text-white">
              Channel Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="channelName"
              type="text"
              placeholder="e.g., Premium Content"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              maxLength={64}
              className={`!bg-[#1f2a3c] dark:!bg-[#1f2a3c] border-gray-700 text-white placeholder:text-gray-500 selection:bg-blue-600 selection:text-white h-11 focus-visible:!bg-[#243247] dark:focus-visible:!bg-[#243247] ${
                errors.channelName ? 'border-red-500' : ''
              }`}
            />
            {errors.channelName && (
              <p className="text-xs text-red-400 mt-1">{errors.channelName}</p>
            )}
            <p className="text-xs text-gray-500">{channelName.length}/64 characters</p>
          </div>

          {/* Status */}
          <div className="space-y-3 max-w-md">
            <Label htmlFor="status" className="text-sm font-semibold text-white">
              Status
            </Label>
            <Select value={status} onValueChange={(v) => setStatus(v as any)}>
              <SelectTrigger
                id="status"
                className="bg-gray-800 border-gray-700 text-white h-11"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-800">
                <SelectItem value="Active" className="text-white focus:bg-gray-800">
                  Active
                </SelectItem>
                <SelectItem value="Suspended" className="text-white focus:bg-gray-800">
                  Suspended
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-gray-800 px-6 sm:px-8 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={onDelete}
              className="flex-1 border-red-500 bg-red-600 text-white hover:bg-red-500"
            >
              Delete Channel
            </Button>
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 border-gray-700 text-black hover:bg-gray-800 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}